import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import {
  EventAvailable as TotalIcon,
  CheckCircle as ArrivedIcon,
  LocalHospital as ConsultationIcon,
  Done as FinishedIcon,
  Cancel as CanceledIcon,
  AccessTime as WaitingIcon
} from '@mui/icons-material';
import type { ReceptionStats } from '@app-types/reception';

interface StatsBarProps {
  stats: ReceptionStats | null;
  loading?: boolean;
}

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, color, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            backgroundColor: `${color}.100`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon sx={{ color: `${color}.main`, fontSize: 32 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={600} color={color}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

export const StatsBar: React.FC<StatsBarProps> = ({ stats, loading }) => {
  if (loading || !stats) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const avgWaitingFormatted = stats.avgWaitingMinutes 
    ? `${Math.round(stats.avgWaitingMinutes)} min`
    : 'N/A';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 2 }}>
      <StatCard
        icon={TotalIcon}
        label="Total programari"
        value={stats.total}
        color="primary"
      />
      <StatCard
        icon={ArrivedIcon}
        label="Pacienti sositi"
        value={stats.arrived}
        color="success"
      />
      <StatCard
        icon={ConsultationIcon}
        label="In consultatie"
        value={stats.inConsultation}
        color="warning"
      />
      <StatCard
        icon={FinishedIcon}
        label="Finalizate"
        value={stats.finished}
        color="success"
      />
      <StatCard
        icon={CanceledIcon}
        label="Anulate"
        value={stats.canceled}
        color="error"
        subtitle={`${stats.noShow} absent`}
      />
      <StatCard
        icon={WaitingIcon}
        label="Timp asteptare"
        value={avgWaitingFormatted}
        color="info"
        subtitle="mediu"
      />
    </Box>
  );
};

